<script setup>
import { computed, ref } from "vue";
import { IconStar, IconStarFilled } from "@tabler/icons-vue";

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
    modelValue: { type: Number, default: 0 },
    mode: { type: String, default: "star" },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    labels: {
        type: Array,
        default: () => ["Very poor", "Poor", "Okay", "Good", "Excellent"],
    },
    emojis: {
        type: Array,
        default: () => ["😠", "😟", "😐", "😊", "😍"],
    },
});

const value = computed({
    get: () => props.modelValue,
    set: (next) => emit("update:modelValue", next),
});
const hoverValue = ref(0);
const displayValue = computed(() => hoverValue.value || value.value);

function selectRating(rating) {
    if (props.disabled) return;
    value.value = value.value === rating ? 0 : rating;
}

function setHoverRating(rating) {
    if (props.disabled || props.mode !== "star") return;
    hoverValue.value = rating;
}

function clearHoverRating() {
    hoverValue.value = 0;
}
</script>

<template>
    <div
        class="rating-input"
        :class="[
            `rating-input--${mode}`,
            { 'rating-input--invalid': invalid },
        ]"
        role="radiogroup"
        aria-label="Rating"
    >
        <button
            v-for="rating in 5"
            :key="rating"
            type="button"
            class="rating-input__button"
            :class="{
                'is-selected': value === rating,
                'is-active': displayValue >= rating,
                'is-hovered': hoverValue >= rating,
            }"
            :disabled="disabled"
            role="radio"
            :aria-checked="value === rating"
            :aria-label="`${rating} out of 5 - ${labels[rating - 1]}`"
            @click="selectRating(rating)"
            @mouseenter="setHoverRating(rating)"
            @focus="setHoverRating(rating)"
            @mouseleave="clearHoverRating"
            @blur="clearHoverRating"
        >
            <template v-if="mode === 'emoji'">
                {{ emojis[rating - 1] }}
            </template>
            <template v-else>
                <IconStarFilled
                    v-if="displayValue >= rating"
                    :size="28"
                    stroke-width="1.8"
                />
                <IconStar
                    v-else
                    :size="28"
                    stroke-width="1.8"
                />
            </template>
        </button>
    </div>
</template>

<style scoped>
.rating-input {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
}

.rating-input__button {
    min-width: 2.75rem;
    height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-color, #ded2c1);
    border-radius: 0.75rem;
    background: #fff;
    color: var(--text-muted, #807363);
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.rating-input__button:hover:not(:disabled),
.rating-input__button:focus-visible {
    border-color: var(--primary-darkest, #30291f);
    background: var(--background-secondary, #f3eee7);
    color: var(--primary-darkest, #30291f);
    outline: none;
}

.rating-input__button.is-selected {
    border-color: var(--primary-darkest, #30291f);
    background: var(--background-secondary, #f3eee7);
    color: var(--primary-darkest, #30291f);
}

.rating-input__button:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.rating-input--emoji {
    gap: 16px;
    justify-content: center;
}

.rating-input--emoji .rating-input__button {
    flex: 1;
    padding: 0.75rem;
    font-size: 1.5rem;
    min-width: 0;
}

.rating-input--star {
    justify-content: flex-start;
    gap: 0.6rem;
}

.rating-input--star .rating-input__button {
    width: 2.65rem;
    min-width: 2.65rem;
    height: 2.65rem;
    border-radius: 0.75rem;
    color: var(--text-muted, #807363);
}

.rating-input--star .rating-input__button.is-active,
.rating-input--star .rating-input__button.is-hovered,
.rating-input--star .rating-input__button:hover:not(:disabled),
.rating-input--star .rating-input__button:focus-visible {
    color: var(--primary-darkest, #30291f);
}

.rating-input--star .rating-input__button.is-selected,
.rating-input--star .rating-input__button.is-hovered {
    border-color: var(--primary-darkest, #30291f);
    background: var(--background-secondary, #f3eee7);
}

.rating-input--invalid .rating-input__button {
    border-color: #fca5a5;
}

@media (max-width: 480px) {
    .rating-input {
        gap: 6px;
    }

    .rating-input__button {
        min-width: 2.5rem;
        height: 2.5rem;
    }
}
</style>
